import React from "react";
import dayjs from "dayjs";
import {
  getFormBuilderAction,
  getClientFormBuilderAction,
} from "../../../../../../../../store/action/admin/dimensionsEntityActions";
import { getAllTradeLicensesAction } from "../../../../../../../../store/action/admin/tradeLicenseAction";

export const useFormEffects = ({
  open,
  isCreateMode,
  form,
  entityId,
  dimensionId,
  dispatch,
  dynamicFormTemplate,
  clientFormBuilderList,
}) => {
  React.useEffect(() => {
    if (open) {
      dispatch(getFormBuilderAction("Dimension-Clients")).catch(() => {});

      form?.resetFields?.();
      if (isCreateMode) {
        if (entityId) {
          dispatch(getAllTradeLicensesAction()).catch(() => {});
        }

        form.setFieldsValue({
          customerId: undefined,
          customerName: undefined,
          customerEmail: undefined,
          customerWebsite: undefined,
          customerPhone: undefined,
          tradeLicenseId: undefined,
          Address1: undefined,
          Address2: undefined,
          state: undefined,
          city: undefined,
          country: undefined,
          postalCode: undefined,
          pointOfContacts: [{}],
          formQuestions: undefined,
          formTemplateId: undefined,
        });
      } else {
        dispatch(getAllTradeLicensesAction()).catch(() => {});
      }
    }
  }, [open, isCreateMode, form, entityId, dispatch]);

  React.useEffect(() => {
    if (
      !isCreateMode &&
      clientFormBuilderList?.customerDetails?.formTemplateId
    ) {
      const formTemplateId =
        clientFormBuilderList.customerDetails.formTemplateId;
      dispatch(getFormBuilderAction("Dimension-Clients")).catch(() => {});
    }
  }, [isCreateMode, clientFormBuilderList, dispatch]);

  React.useEffect(() => {
    if (
      dynamicFormTemplate &&
      Array.isArray(dynamicFormTemplate) &&
      dynamicFormTemplate.length > 0 &&
      dynamicFormTemplate[0]?.questions &&
      dynamicFormTemplate[0].questions.length > 0 &&
      isCreateMode
    ) {
      const template = dynamicFormTemplate[0];
      const initialQuestions = template.questions.map((q) => ({
        questionId: q._id,
        answerType: q.answerType,
        answer: null,
      }));

      const initialValues = {
        formTemplateId: template._id,
        formQuestions: initialQuestions,
      };

      template.questions.forEach((q, idx) => {
        initialValues[`formQuestions[${idx}].answer`] = null;
        initialValues[`formQuestions[${idx}].questionId`] = q._id;
        initialValues[`formQuestions[${idx}].answerType`] = q.answerType;
      });

      form.setFieldsValue(initialValues);
    }
  }, [dynamicFormTemplate, form, isCreateMode]);

  React.useEffect(() => {
    if (isCreateMode) return;

    if (dimensionId) {
      dispatch(getClientFormBuilderAction(dimensionId)).catch(() => {});
    }
  }, [dimensionId, dispatch, isCreateMode]);

  React.useEffect(() => {
    if (isCreateMode) return;
    if (!clientFormBuilderList?.customerDetails) return;

    const customerDetails = clientFormBuilderList.customerDetails;

    const customerPhone =
      customerDetails.phone?.countryCode && customerDetails.phone?.number
        ? `${customerDetails.phone.countryCode}${customerDetails.phone.number}`
        : undefined;

    const pointOfContacts =
      customerDetails.pointOfContacts?.map((p) => ({
        firstName: p?.name?.firstName || undefined,
        lastName: p?.name?.lastName || undefined,
        workEmail: p?.workEmail || undefined,
        phone:
          p?.phoneNumber?.countryCode && p?.phoneNumber?.number
            ? `${p.phoneNumber.countryCode}${p.phoneNumber.number}`
            : undefined,
        isPrimary: Boolean(p?.primary),
      })) || undefined;

    let formQuestions = undefined;
    let formTemplateId = undefined;
    if (
      clientFormBuilderList.customerDetails.questions &&
      Array.isArray(clientFormBuilderList.customerDetails.questions) &&
      clientFormBuilderList.customerDetails.questions.length > 0
    ) {
      const template =
        dynamicFormTemplate &&
        Array.isArray(dynamicFormTemplate) &&
        dynamicFormTemplate.length > 0
          ? dynamicFormTemplate[0]
          : null;

      formQuestions = clientFormBuilderList.customerDetails.questions.map(
        (q) => {
          let answerValue = null;

          const originalQuestion = template?.questions?.find(
            (tq) => tq._id === (q.questionId || q._id)
          );
          const originalAnswerType =
            originalQuestion?.answerType || q.answerType;

          if (q.answer?.text !== null && q.answer?.text !== undefined) {
            const textValue = String(q.answer.text);
            answerValue =
              textValue === "null" || textValue === "undefined"
                ? undefined
                : textValue;
          } else if (q.answer?.date !== null && q.answer?.date !== undefined) {
            try {
              let dateInput = null;

              if (
                typeof q.answer.date === "string" &&
                q.answer.date.trim() !== "" &&
                q.answer.date !== "null"
              ) {
                dateInput = q.answer.date;
              } else if (
                typeof q.answer.date === "number" &&
                !isNaN(q.answer.date)
              ) {
                dateInput = q.answer.date;
              } else if (q.answer.date instanceof Date) {
                dateInput = q.answer.date;
              } else if (dayjs.isDayjs(q.answer.date)) {
                // Already a dayjs object
                answerValue = q.answer.date.isValid() ? q.answer.date : null;
                return {
                  questionId: q.questionId || q._id,
                  answerType: q.answerType,
                  answer: answerValue,
                };
              }

              if (dateInput !== null) {
                const parsed = dayjs(dateInput);
                answerValue = parsed.isValid() ? parsed : null;
              } else {
                answerValue = null;
              }
            } catch (error) {
              answerValue = null;
            }
          } else if (q.answer?.file) {
            if (q.answer.file.url || q.answer.file.publicId) {
              answerValue = [q.answer.file];
            } else if (Array.isArray(q.answer.file)) {
              answerValue = q.answer.file;
            } else {
              answerValue = q.answer.file ? [q.answer.file] : [];
            }
          } else if (q.answer?.choices) {
            const multiOptions = originalQuestion?.multiOptions || [];

            const convertTextToId = (textValue) => {
              if (!textValue) return null;

              const option = multiOptions.find(
                (opt) =>
                  opt.text === textValue ||
                  String(opt.text) === String(textValue)
              );

              return option?._id || textValue;
            };

            let choicesArray = [];
            if (Array.isArray(q.answer.choices)) {
              choicesArray = q.answer.choices.map((choice) =>
                convertTextToId(choice)
              );
            } else if (q.answer.choices) {
              choicesArray = [convertTextToId(q.answer.choices)];
            }

            if (originalAnswerType === "radio") {
              // Radio expects a single value
              if (choicesArray.length > 0) {
                answerValue = choicesArray[0];
              } else {
                answerValue = undefined;
              }
            } else if (
              originalAnswerType === "checkbox" ||
              originalAnswerType === "multiple-choice"
            ) {
              answerValue = choicesArray.filter(
                (id) => id !== null && id !== undefined
              );
            } else if (originalAnswerType === "dropDown") {
              if (choicesArray.length > 0) {
                answerValue = choicesArray[0];
              } else {
                answerValue = undefined;
              }
            } else {
              answerValue = choicesArray.filter(
                (id) => id !== null && id !== undefined
              );
            }
          }

          return {
            questionId: q.questionId || q._id,
            answerType: q.answerType,
            answer: answerValue,
          };
        }
      );
      formTemplateId =
        clientFormBuilderList.formTemplateId ||
        clientFormBuilderList.customerDetails.formTemplateId;
    }

    const formValues = {
      customerId: customerDetails.customerId || undefined,
      customerName:
        customerDetails.legalName || customerDetails.displayName || undefined,
      customerEmail: customerDetails.email || undefined,
      customerWebsite: customerDetails.website || undefined,
      customerPhone: customerPhone,
      pointOfContacts: pointOfContacts,
      tradeLicenseId: customerDetails.tradeLicenseId
        ? String(customerDetails.tradeLicenseId)
        : undefined,
    };

    const address =
      customerDetails.customerAddress ||
      customerDetails.shippingAddress ||
      customerDetails.billingAddress;
    if (address) {
      formValues.Address1 = address.address || address.address1 || undefined;
      formValues.Address2 =
        address.secondAddress || address.address2 || undefined;
      formValues.state = address.state || undefined;
      formValues.city = address.city || undefined;
      formValues.country = address.country || undefined;
      formValues.postalCode =
        address.postalCode || address.zipCode || undefined;
    }

    if (formTemplateId) {
      formValues.formTemplateId = formTemplateId;
    }
    if (formQuestions) {
      formValues.formQuestions = formQuestions;
    }

    form.setFieldsValue(formValues);

    if (formValues.tradeLicenseId) {
      setTimeout(() => {
        const currentValue = form.getFieldValue("tradeLicenseId");

        if (!currentValue || currentValue !== formValues.tradeLicenseId) {
          form.setFieldValue("tradeLicenseId", formValues.tradeLicenseId);
        }
      }, 100);
    }
  }, [clientFormBuilderList, form, isCreateMode]);
};
